/**
 * SolutionTypeFlowDiagram
 * Genera automáticamente un diagrama SVG del flujo de decisión de un Tipo de Solución
 * basado en su configuración (agendamiento, materiales, notificación, IA+GPS, devolución, WhatsApp).
 * 100% dinámico: si el cliente cambia las reglas, el diagrama cambia solo.
 */
import { useRef, useState } from "react";
import { FlowBox, FlowDecision, FlowArrow, ArrowMarker, FLOW_COLORS } from "./FlowPrimitives";
import { Download, Loader2 } from "lucide-react";

// ============ Función pura para construir el layout del flujo ============
// Exportada para poder generarla en PDFs / batch exports sin renderizar en el DOM.
export function buildFlow(item) {
  const nodes = [];
  const arrows = [];
  // Layout vertical con columnas
  const CX = 400; // centro X
  let y = 30;
  const STEP = 90;

  // 1. Inicio (muestra jerarquía Línea > Servicio > Actividad)
  const startLabel = item.service_name || "Crear Orden";
  const startSubLabel = item.activity_name && item.activity_name !== item.name
    ? `${item.code} · ${item.activity_name}`
    : item.code;
  nodes.push({ key: "start", x: CX - 100, y, w: 200, h: 52, type: "start", label: startLabel, sublabel: startSubLabel });
  y += 72;

  // 2. Cliente
  arrows.push({ from: [CX, nodes[nodes.length - 1].y + 46], to: [CX, y] });
  nodes.push({
    key: "client",
    x: CX - 120, y, w: 240, h: 46, type: "process",
    label: "Cliente + Suscripción",
    sublabel: `Aplica a: ${(item.applies_to_client_types || []).slice(0, 3).join(", ")}${item.applies_to_client_types?.length > 3 ? "…" : ""}`
  });
  y += 65;

  // 3. Evento masivo (siempre chequea)
  arrows.push({ from: [CX, y - 19], to: [CX, y] });
  nodes.push({
    key: "event", x: CX - 100, y, w: 200, h: 60, type: "decision",
    label: "¿Evento masivo?"
  });
  const eventNodeY = y;
  y += 80;

  // 4. Si evento -> bloqueado (a la derecha)
  arrows.push({
    from: [CX + 100, eventNodeY + 30],
    to: [CX + 260, eventNodeY + 30],
    label: "SÍ", color: "#EF4444"
  });
  nodes.push({
    key: "blocked", x: CX + 260, y: eventNodeY + 8, w: 150, h: 44, type: "danger",
    label: "BLOQUEADO",
    sublabel: "Esperar resolución"
  });

  // 5. Agendamiento (según scheduling_mode)
  arrows.push({ from: [CX, eventNodeY + 60], to: [CX, y], label: "NO" });
  const schedLabel = item.scheduling_mode === "automatic" ? "Agendamiento AUTOMÁTICO" : "Agendamiento MANUAL";
  const schedSublabel = item.whatsapp_confirmation
    ? "Confirma vía WhatsApp"
    : (item.scheduling_mode === "automatic" ? "Sistema selecciona técnico" : "Agendador humano");
  nodes.push({
    key: "sched", x: CX - 130, y, w: 260, h: 50, type: "process",
    label: schedLabel, sublabel: schedSublabel
  });
  y += 65;

  // 6. Asignación técnico
  arrows.push({ from: [CX, y - 15], to: [CX, y] });
  nodes.push({
    key: "assign", x: CX - 100, y, w: 200, h: 44, type: "process",
    label: "Asignar Técnico",
    sublabel: "Filtrar por zona · SLA"
  });
  y += 60;

  // 7. SLA
  arrows.push({ from: [CX, y - 16], to: [CX, y] });
  nodes.push({
    key: "sla", x: CX - 150, y, w: 300, h: 50, type: "ia",
    label: `SLA P1/P2/P3: ${item.sla_p1_hours}h · ${item.sla_p2_hours}h · ${item.sla_p3_hours}h`,
    sublabel: "Notifica al técnico si Por Vencer / Retrasado"
  });
  y += 70;

  // 8. Ejecución
  arrows.push({ from: [CX, y - 20], to: [CX, y] });
  nodes.push({
    key: "exec", x: CX - 100, y, w: 200, h: 44, type: "process",
    label: "Técnico en Campo"
  });
  y += 60;

  // 9. Materiales — decisión según material_control
  if (item.material_control !== "no_aplica") {
    arrows.push({ from: [CX, y - 16], to: [CX, y] });
    const matColors = {
      maximo: "danger",
      minimo: "danger",
      planificado: "ia",
      gratuito: "success",
    };
    const matLabels = {
      maximo: "Materiales · MÁXIMO obligatorio",
      minimo: "Materiales · MÍNIMO obligatorio",
      planificado: "Materiales · Planificado",
      gratuito: "Materiales · Gratuito",
    };
    const matSub = {
      maximo: "No puede descargar más del máx",
      minimo: "No puede descargar menos del mín",
      planificado: "Definido al crear la orden",
      gratuito: "Sin control de cantidades",
    };
    nodes.push({
      key: "material", x: CX - 150, y, w: 300, h: 50,
      type: matColors[item.material_control] || "process",
      label: matLabels[item.material_control],
      sublabel: matSub[item.material_control]
    });
    y += 70;
  }

  // 10. Devolución de equipo (si requiere)
  if (item.requires_equipment_return) {
    arrows.push({ from: [CX, y - 20], to: [CX, y] });
    nodes.push({
      key: "return", x: CX - 130, y, w: 260, h: 50, type: "danger",
      label: "OBLIGA devolver equipo",
      sublabel: "Registrar N° serie retirado"
    });
    y += 70;
  }

  // 11. Fotos + IA + GPS (decisión)
  if (item.photo_report_required || item.ai_gps_validation) {
    arrows.push({ from: [CX, y - 20], to: [CX, y] });
    const label = item.ai_gps_validation
      ? "Fotos + Validación IA + GPS"
      : "Reporte Fotográfico";
    const sublabel = item.ai_gps_validation
      ? "IA detecta desfases · GPS >50m → obliga corregir coord"
      : "Fiscalizador revisa fotos";
    nodes.push({
      key: "photo", x: CX - 150, y, w: 300, h: 50,
      type: item.ai_gps_validation ? "ia" : "process",
      label, sublabel
    });
    y += 70;
  }

  // 12. Notificación cliente
  if (item.client_notification !== "none") {
    arrows.push({ from: [CX, y - 20], to: [CX, y] });
    const nLabel = item.client_notification === "always"
      ? "Notifica al cliente SIEMPRE"
      : "Notifica solo Servicio/Corp";
    nodes.push({
      key: "notif", x: CX - 130, y, w: 260, h: 44, type: "process",
      label: nLabel,
      sublabel: item.whatsapp_confirmation ? "Vía WhatsApp" : "Vía email"
    });
    y += 60;
  }

  // 13. Validación (ATC / Fiscalizador)
  arrows.push({ from: [CX, y - 16], to: [CX, y] });
  const valSub = item.requires_atc_validation
    ? "ATC valida cobro · Fiscalizador valida técnica"
    : "Fiscalizador revisa evidencias";
  nodes.push({
    key: "validate", x: CX - 130, y, w: 260, h: 50, type: "process",
    label: "Validación Post-Ejecución", sublabel: valSub
  });
  y += 70;

  // 14. PVP configurable (si aplica)
  if (item.pvp_configurable) {
    arrows.push({ from: [CX, y - 20], to: [CX, y] });
    nodes.push({
      key: "pvp", x: CX - 120, y, w: 240, h: 44, type: "ia",
      label: "Cobro variable (PVP en orden)",
      sublabel: "Configurable por caso"
    });
    y += 60;
  }

  // 15. Retiro futuro (si aplica)
  if (item.creates_future_task) {
    arrows.push({ from: [CX, y - 16], to: [CX, y] });
    nodes.push({
      key: "future", x: CX - 130, y, w: 260, h: 46, type: "ia",
      label: `Auto-crear tarea: ${item.creates_future_task.toUpperCase()}`,
      sublabel: "En fecha futura planificada"
    });
    y += 62;
  }

  // 16. Cierre
  arrows.push({ from: [CX, y - 18], to: [CX, y] });
  nodes.push({
    key: "end", x: CX - 90, y, w: 180, h: 44, type: "end", label: "Orden Cerrada"
  });
  y += 60;

  return { nodes, arrows, height: y + 20 };
}


export default function SolutionTypeFlowDiagram({ item, compact = false }) {
  const svgRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const { nodes, arrows, height } = buildFlow(item);
  const WIDTH = 800;

  const downloadPNG = async () => {
    if (!svgRef.current) return;
    setDownloading(true);
    try {
      const svg = svgRef.current;
      // Clonar y agregar background blanco explícito
      const clone = svg.cloneNode(true);
      const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      bg.setAttribute("width", "100%");
      bg.setAttribute("height", "100%");
      bg.setAttribute("fill", "white");
      clone.insertBefore(bg, clone.firstChild);
      const xml = new XMLSerializer().serializeToString(clone);
      const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = WIDTH * 2;
        canvas.height = height * 2;
        const ctx = canvas.getContext("2d");
        ctx.scale(2, 2);
        ctx.drawImage(img, 0, 0);
        const png = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = png;
        link.download = `flujo-${item.code}.png`;
        link.click();
        URL.revokeObjectURL(url);
        setDownloading(false);
      };
      img.onerror = () => { setDownloading(false); };
      img.src = url;
    } catch (e) {
      setDownloading(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div className="min-w-0 flex-1">
          {/* Breadcrumb jerárquico Feb-2026: Línea de Negocio › Servicio › Actividad */}
          {(item.line_of_business || item.service_name) && (
            <div className="flex items-center flex-wrap gap-1.5 mb-1.5 text-[10px] font-mono uppercase tracking-wider">
              {item.line_of_business && (
                <span className="px-1.5 py-0.5 rounded bg-[#0A3747] text-[#E0E621] font-bold">
                  {(item.line_of_business || "").replace(/_/g, " ")}
                </span>
              )}
              {item.service_name && (
                <>
                  <span className="text-gray-400">›</span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[#0A3747] font-semibold">
                    {item.service_name}
                  </span>
                </>
              )}
              {(item.activity_name && item.activity_name !== item.name) && (
                <>
                  <span className="text-gray-400">›</span>
                  <span className="px-1.5 py-0.5 rounded border border-gray-300 text-gray-600">
                    {item.activity_name}
                  </span>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] text-gray-500">{item.code}</span>
            {item.technology && (
              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 uppercase" title="Tecnología (parámetro)">
                {item.technology.replace(/_/g, " ")}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-lg mt-0.5" style={{ fontFamily: "Chivo" }}>
            {item.activity_name || item.name}
          </h3>
        </div>
        {!compact && (
          <button
            onClick={downloadPNG}
            disabled={downloading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A3747] text-white text-xs rounded hover:bg-[#135166] disabled:opacity-40 flex-shrink-0"
            data-testid="download-flow-png"
          >
            {downloading ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />}
            Descargar PNG
          </button>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-auto max-h-[70vh]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${height}`}
          xmlns="http://www.w3.org/2000/svg"
          className="w-full"
          style={{ minHeight: Math.min(height, 700) }}
        >
          <ArrowMarker />
          {arrows.map((a, i) => (
            <FlowArrow key={`a${i}`} from={a.from} to={a.to} label={a.label} color={a.color} testId={`flow-arrow-${i}`} />
          ))}
          {nodes.map((n) => (
            n.type === "decision" ? (
              <FlowDecision key={n.key} x={n.x} y={n.y} w={n.w} h={n.h} label={n.label} testId={`flow-node-${n.key}`} />
            ) : (
              <FlowBox key={n.key} x={n.x} y={n.y} w={n.w} h={n.h} type={n.type} label={n.label} sublabel={n.sublabel} testId={`flow-node-${n.key}`} />
            )
          ))}
        </svg>
      </div>

      {/* Leyenda */}
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-gray-600">
        <LegendDot color="#0A3747" label="Inicio / Fin" />
        <LegendDot color="#F59E0B" label="Decisión" />
        <LegendDot color="#EF4444" label="Regla obligatoria" />
        <LegendDot color="#E0E621" label="Validación IA" />
        <LegendDot color="#10B981" label="Éxito / gratuito" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}

// ============ Utilidades para generar SVG standalone (usado en PDF batch) ============
export function buildSvgString(item, width = 800) {
  const { nodes, arrows, height } = buildFlow(item);
  const parts = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">`
  );
  // Background blanco explícito
  parts.push(`<rect width="100%" height="100%" fill="white"/>`);
  // Marker de flecha
  parts.push(
    `<defs><marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#0A3747"/></marker></defs>`
  );

  // Flechas
  for (const a of arrows) {
    const [x1, y1] = a.from;
    const [x2, y2] = a.to;
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    const color = a.color || "#0A3747";
    parts.push(
      `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="1.6" marker-end="url(#arrow)"/>`
    );
    if (a.label) {
      parts.push(
        `<rect x="${midX - 12}" y="${midY - 8}" width="24" height="14" fill="white" stroke="${color}" stroke-width="0.8" rx="2"/>` +
        `<text x="${midX}" y="${midY + 3}" text-anchor="middle" font-size="9" fill="${color}" font-family="JetBrains Mono, monospace" font-weight="600">${escapeXml(a.label)}</text>`
      );
    }
  }

  // Nodos
  for (const n of nodes) {
    if (n.type === "decision") {
      const c = FLOW_COLORS.decision;
      const cx = n.x + n.w / 2;
      const cy = n.y + n.h / 2;
      parts.push(
        `<polygon points="${cx},${n.y} ${n.x + n.w},${cy} ${cx},${n.y + n.h} ${n.x},${cy}" fill="${c.fill}" stroke="${c.stroke}" stroke-width="1.6"/>` +
        `<text x="${cx}" y="${cy + 4}" text-anchor="middle" fill="${c.text}" font-size="11" font-family="IBM Plex Sans, sans-serif" font-weight="600">${escapeXml(n.label)}</text>`
      );
    } else {
      const c = FLOW_COLORS[n.type] || FLOW_COLORS.process;
      parts.push(
        `<rect x="${n.x}" y="${n.y}" width="${n.w}" height="${n.h}" rx="6" fill="${c.fill}" stroke="${c.stroke}" stroke-width="1.6"/>`
      );
      const labelY = n.y + n.h / 2 + (n.sublabel ? -4 : 4);
      parts.push(
        `<text x="${n.x + n.w / 2}" y="${labelY}" text-anchor="middle" fill="${c.text}" font-size="11" font-family="IBM Plex Sans, sans-serif" font-weight="600">${escapeXml(n.label)}</text>`
      );
      if (n.sublabel) {
        parts.push(
          `<text x="${n.x + n.w / 2}" y="${n.y + n.h / 2 + 11}" text-anchor="middle" fill="${c.text}" font-size="9" opacity="0.75" font-family="JetBrains Mono, monospace">${escapeXml(n.sublabel)}</text>`
        );
      }
    }
  }
  parts.push("</svg>");
  return parts.join("");
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function svgToPngDataUrl(svgString, targetWidth = 800) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const scale = 2; // retina
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth * scale;
      canvas.height = (img.height / img.width) * targetWidth * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, targetWidth, (img.height / img.width) * targetWidth);
      const dataUrl = canvas.toDataURL("image/png");
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
